import React, { CSSProperties, FC, memo, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import colors from "../constants/colors";
import { View } from "./index";

export enum AvatarShape {
  ROUNDED = "rounded",
  SQUARED = "squared",
}

export enum AvatarSize {
  ExtraSmall = "extrasmall",
  Small = "small",
  Medium = "medium",
  Large = "large",
  ExtraLarge = "extralarge",
}

const ROUNDED_BR = "50%";
const SQUARED_BR = {
  [AvatarSize.ExtraSmall]: 4,
  [AvatarSize.Small]: 10,
  [AvatarSize.Medium]: 10,
  [AvatarSize.Large]: 10,
  [AvatarSize.ExtraLarge]: 10,
};

const useStyles = createUseStyles({
  picWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    position: "relative",
    borderRadius: "50%",
    backgroundSize: "cover !important",
  },
  avatar: {
    backgroundColor: colors.blue,
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 500,
    fontSize: "1.1rem",
    letterSpacing: 0.5,
    justifyContent: "center",
    alignItems: "center",
    color: colors.white,
    flexShrink: 0,
    borderRadius: "50%",
  },
  extrasmall: {
    width: 22,
    height: 22,
  },
  small: {
    width: 44,
    height: 44,
  },
  medium: {
    width: 65,
    height: 65,
  },
  large: {
    width: 200,
    height: 200,
  },
  extralarge: {
    width: 350,
    height: 350,
    fontSize: "7rem",
  },
});

export interface AvatarProps {
  alt?: string;
  url?: string;
  type?: AvatarShape;
  onClick?: () => void;
  style?: CSSProperties;
  size?: AvatarSize;
}

const Avatar: FC<AvatarProps> = ({
  alt,
  url,
  type = AvatarShape.ROUNDED,
  onClick = null,
  style,
  size = AvatarSize.Small,
}) => {
  const defaultBorderRadius =
    type === AvatarShape.ROUNDED ? ROUNDED_BR : SQUARED_BR[size];
  const borderRadius = style?.borderRadius ?? defaultBorderRadius;
  const styles = useStyles();
  const [showPic, setShowPic] = useState(Boolean(url));

  useEffect(() => {
    setShowPic(Boolean(url));
  }, [url]);

  return !showPic ? (
    <View
      className={`${styles.avatar} ${styles[size]}`}
      onClick={onClick}
      style={{ ...style, borderRadius }}
    >
      {alt}
    </View>
  ) : (
    <View
      className={styles[size]}
      onClick={onClick}
      style={{ ...style, borderRadius }}
    >
      <img
        key={url}
        src={url}
        alt={alt}
        onError={() => setShowPic(false)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius,
        }}
      />
    </View>
  );
};

export default memo(Avatar);
